import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  decodeJwtPayload,
  httpClient,
} from '~/lib/http-client'
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SocialLoginResponse,
} from '~/types/auth'
import type { User } from '~/types/user'

/**
 * Verifica se a API está online
 */
export async function healthCheck() {
  return httpClient.get<{ status: string }>('/health/', { useAuth: false })
}

/**
 * Realiza login do usuário
 */
export async function login(credentials: LoginRequest) {
  return httpClient.post<LoginResponse>(
    '/auth/login',
    { ...credentials },
    {
      useAuth: false,
    }
  )
}

/**
 * Registra um novo usuário
 */
export async function register(userData: RegisterRequest) {
  return httpClient.post<RegisterResponse>(
    '/auth/register',
    { ...userData },
    {
      useAuth: false,
    }
  )
}

/**
 * Realiza login com Google OAuth
 */
export async function loginWithGoogle(token: string) {
  return httpClient.post<SocialLoginResponse>(
    '/auth/google',
    { token },
    {
      useAuth: false,
    }
  )
}

/**
 * Realiza login com Microsoft OAuth
 */
export async function loginWithMicrosoft(token: string) {
  return httpClient.post<SocialLoginResponse>(
    '/auth/microsoft',
    { token },
    {
      useAuth: false,
    }
  )
}

/**
 * Solicita recuperação de senha
 */
export async function forgotPassword(email: string) {
  return httpClient.post<{ message: string }>(
    '/auth/forgot-password',
    { email },
    {
      useAuth: false,
    }
  )
}

/**
 * Redefine senha usando token de recuperação
 */
export async function resetPassword(token: string, password: string) {
  return httpClient.post<{ message: string }>(
    '/auth/reset-password',
    { token, password },
    {
      useAuth: false,
    }
  )
}

/**
 * Salva o token no localStorage
 */
export function saveToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token)
}

/**
 * Recupera o token do localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY)
}

/**
 * Remove o token do localStorage
 */
export function removeToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY)
}

/**
 * Salva os dados do usuário no localStorage
 */
export function saveUser(user: User): void {
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user))
}

/**
 * Recupera os dados do usuário do localStorage
 */
export function getUser(): User | null {
  const user = localStorage.getItem(AUTH_USER_KEY)
  return user ? JSON.parse(user) : null
}

/**
 * Remove os dados do usuário do localStorage
 */
export function removeUser(): void {
  localStorage.removeItem(AUTH_USER_KEY)
}

/**
 * Limpa todos os dados de autenticação
 */
export function clearAuth(): void {
  removeToken()
  removeUser()
}

/**
 * Verifica se o token está expirado
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload) {
    return true
  }

  const expSecondsRaw =
    (payload as { exp?: number | string }).exp ?? (payload as any)['exp']
  const expSeconds = Number(expSecondsRaw)

  if (!Number.isFinite(expSeconds)) {
    return true
  }

  return Date.now() >= expSeconds * 1000
}

/**
 * Verifica se o usuário está autenticado
 */
export function isAuthenticated(): boolean {
  const token = getToken()
  if (!token) return false
  return !isTokenExpired(token)
}
